import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  LinearProgress,
  alpha,
  Paper,
  useMediaQuery,
  useTheme,
  Checkbox,
  Popover,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  CalendarToday,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  dashboardApi,
  billUpdate,
  billFreightList,
  billSettlement,
} from "../../Services/Services";
import StatCard from "./StatCard";
import {
  IconTruck,
  IconTruckDelivery,
  IconCalendarStats,
  IconFileInvoice,
  IconReceipt,
  IconReceipt2,
  IconFileAnalytics,
  IconCashOff,
} from "@tabler/icons-react";

import BusinessOverviewChart from "./BusinessOverviewChart";
import SummaryCard from "./SummaryCard";
import LRStatusDonut from "./LRStatusDonut";
import { T, brand, neutral, surface, text } from "../../theme";
const brandColor = brand.main;
const brandBg = alpha(brandColor, 0.05);
const textColor = text.primary;

const noRadius = { borderRadius: "0px !important" };
const noBorder = { border: `1px solid ${neutral[200]}` };
const noShadow = { boxShadow: "none" };
const noBorderB = { borderBottom: `0.8px solid ${neutral[400]}` };
const stickyBlur = {
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    ...noRadius,
    bgcolor: alpha(brandColor, 0.04),
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: brandColor },
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";
const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

/* T is now imported from ../../theme */

const sq = { borderRadius: "6px !important" };

// ─── MOBILE BILL CARD ──────────────────────────────────────────────────
const MobileBillCard = ({
  item,
  idx,
  expanded,
  onToggle,
  onView,
  onEdit,
  onSettle,
  getBranchCode,
}) => {
  const daysDiff = Math.ceil(
    (new Date() - new Date(item.created_at)) / (1000 * 60 * 60 * 24),
  );
  const accentColor = daysDiff > 30 ? brand.main : brand.main;

  return (
    <Paper
      sx={{
        mb: 1,
        borderRadius: "0px",
        border: `1px solid ${neutral[200]}`,
        boxShadow: "none",
        bgcolor: "#f8faff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          minHeight: 75,
          borderLeft: expanded
            ? "4px solid #f59e0b"
            : `4px solid ${accentColor}`,
          transition: "border-color 0.2s",
        }}
      >
        <Box
          sx={{
            width: 70,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#eef2ff",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <ReceiptIcon sx={{ fontSize: 26, color: brandColor }} />
          <Typography
            sx={{
              fontSize: "0.6rem",
              fontWeight: 600,
              color: brandColor,
              mt: 0.5,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              label={getBranchCode(item)}
              size="small"
              sx={{
                borderRadius: "6px !important",
                bgcolor: item.is_custom_bill ? "#DBEAFE" : "#eef2ff",
                color: item.is_custom_bill ? "#1E40AF" : brandColor,
                fontSize: "0.55rem",
                height: 18,
                width: "fit-content",
              }}
            />
          </Stack>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 3 }}>
            <CalendarToday sx={{ fontSize: 10, color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>
              {formatDate(item.created_at)} · {daysDiff}d
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 110,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.55rem", color: "#6b7280" }}>
              Balance
            </Typography>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#dc2626" }}
            >
              {formatCurrency(item.rem_amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              borderRadius: "0px",
              border: `1px solid ${brandColor}`,
              color: brandColor,
              fontSize: "0.6rem",
              textTransform: "none",
              px: 1,
              py: 0.25,
              minHeight: 24,
              bgcolor: "#ffffff",
              "& .MuiButton-startIcon": { marginRight: "4px", marginLeft: 0 },
              "&:hover": { bgcolor: "#eef2ff" },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>
      {expanded && (
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderTop: "1px solid #e0e0e0",
            p: 1.5,
            borderLeft: "4px solid #f59e0b",
          }}
        >
          <Stack spacing={1}>
            <Box
              sx={{
                p: 0.75,
                bgcolor: "#f8faff",
                borderLeft: `3px solid ${brandColor}`,
              }}
            >
              <Grid container spacing={0.5}>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>
                    Total
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    {formatCurrency(item.total_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>
                    TDS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#dc2626",
                    }}
                  >
                    {formatCurrency(item.tds_amount)}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>
                    Deduction
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#ea580c",
                    }}
                  >
                    {formatCurrency(item.deduction_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>
                    Received
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#10b981",
                    }}
                  >
                    {formatCurrency(item.paid_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>
                    Balance
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#dc2626",
                    }}
                  >
                    {formatCurrency(item.rem_amount)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            {(item.follow_date ||
              item.remarks ||
              item.payment_remarks ||
              item.deduction_remarks) && (
              <Stack spacing={0.6}>
                {item.follow_date && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1,
                      py: 0.6,
                      bgcolor: "#fffbeb",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 11, color: "#b45309" }} />
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        color: "#b45309",
                        fontWeight: 700,
                      }}
                    >
                      Follow Up:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: "#78350f",
                        fontWeight: 600,
                      }}
                    >
                      {formatDate(item.follow_date)}
                    </Typography>
                  </Box>
                )}
                {item.remarks && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1,
                      py: 0.6,
                      bgcolor: "#fffbeb",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        color: "#b45309",
                        fontWeight: 700,
                      }}
                    >
                      Remark
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: "#78350f",
                        fontWeight: 600,
                      }}
                    >
                      {item.remarks}
                    </Typography>
                  </Box>
                )}
                {item.payment_remarks && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1,
                      py: 0.6,
                      bgcolor: "#fffbeb",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        color: "#b45309",
                        fontWeight: 700,
                      }}
                    >
                      Payment Remark:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: "#78350f",
                        fontWeight: 600,
                      }}
                    >
                      {item.payment_remarks}
                    </Typography>
                  </Box>
                )}
                {item.deduction_remarks && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.6,
                      px: 1,
                      py: 0.6,
                      bgcolor: "#FFF7ED",
                      border: "1px solid #FED7AA",
                    }}
                  >
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontSize: "0.6rem",
                        color: "#C2410C",
                        fontWeight: 700,
                      }}
                    >
                      Deduction:
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: "#9A3412",
                        fontWeight: 600,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item.deduction_remarks}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px dashed #e0e0e0",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              startIcon={<Visibility sx={{ fontSize: 14 }} />}
              onClick={() => onView(item.billing_id)}
              sx={{
                borderRadius: "0px",
                bgcolor: "#f3f0ff",
                color: "#8b5cf6",
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1,
                "&:hover": { bgcolor: "#ede9fe" },
              }}
            >
              View
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(item)}
              sx={{
                borderRadius: "0px",
                bgcolor: "#eef2ff",
                color: brandColor,
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1,
                "&:hover": { bgcolor: "#e0e7ff" },
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
              onClick={(e) => onSettle(e, item.billing_id)}
              sx={{
                borderRadius: "0px",
                bgcolor: "#ecfdf5",
                color: "#10b981",
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1,
                "&:hover": { bgcolor: "#d1fae5" },
              }}
            >
              Settle
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ─── ACTION BUTTON ─────────────────────────────────────────────────────
const ActionButton = ({ tip, icon, color, onClick }) => {
  const isView = color === "#8b5cf6";
  const c = isView ? "#0891B2" : "#EC4899";
  const bg = isView ? "#ECFEFF" : "#FCE7F3";
  const bd = isView ? "#A5F3FC" : "#FBCFE8";
  const hBg = isView ? "#CFFAFE" : "#FBCFE8";

  return (
    <Tooltip title={tip} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            width: 24,
            height: 24,
            ...sq,
            color: c,
            bgcolor: bg,
            border: `1px solid ${bd}`,
            transition: "all 0.18s",
            "&:hover": {
              bgcolor: hBg,
              borderColor: c,
              transform: "translateY(-1px)",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 14 } })}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const SettleConfirmPopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        borderRadius: "0px",
        border: `1px solid ${neutral[200]}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 220 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Settle this Bill?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.65rem",
          color: "#6b7280",
          textAlign: "center",
          mb: 1.5,
        }}
      >
        Marks it fully paid and removes it from pending bills.
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ borderRadius: "0px", border: "1px solid #e0e0e0" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{
            borderRadius: "0px",
            bgcolor: "#10b981",
            color: "#fff",
            "&:hover": { bgcolor: "#059669" },
          }}
        >
          Settle
        </Button>
      </Stack>
    </Box>
  </Popover>
);

// ─── MAIN GRID COMPONENT ─────────────────────────────────────────────
export default function MainGrid() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [bill, setBill] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_freight: 0,
    total_billing: 0,
    today_freight: 0,
    today_billing: 0,
    current_month_freight: 0,
    current_month_billing: 0,
    freight_pending_for_billing: 0,
    billing_pending_for_payment: 0,
    total_amount: 0,
    received_amount: 0,
    pending_amount: 0,
    completed_freight: 0,
    cancelled_freight: 0,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [editData, setEditData] = useState({
    paid_amount: "",
    tds_percent: "", 
    tds_amount: "", 
    deduction_amount: "", 
    deduction_remarks: "", 
    follow_date: "",
    remarks: "",
    payment_remarks: "",
  });
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [rawBillData, setRawBillData] = useState({
    isCustom: false,
    freights: [],
    customItems: [],
    billInfo: null,
  });
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [monthlyOverview, setMonthlyOverview] = useState([]);
  const [currentMonthName, setCurrentMonthName] = useState("");
  const [expandedPartyId, setExpandedPartyId] = useState(null);
  const [settleAnchorEl, setSettleAnchorEl] = useState(null);
  const [settleId, setSettleId] = useState(null);
  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const navigate = useNavigate();
  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const data = await dashboardApi();
        if (data) {
          // Flatten the nested billing data
          const flattenedBills = [];
          data.billing_data?.forEach((party) => {
            party.pending_bills?.forEach((bill) => {
              flattenedBills.push({
                ...bill,
                party_name: party.name,
                party_id: party.party_id,
                branch_name: party.branch_name || "RAIPUR", // Add default if needed
              });
            });
          });
          setBill(flattenedBills);

          // Calculate financial totals from all bills
          const totals = flattenedBills.reduce(
            (acc, b) => ({
              total: acc.total + (Number(b.total_amount) || 0),
              paid: acc.paid + (Number(b.paid_amount) || 0),
              pending: acc.pending + (Number(b.rem_amount) || 0),
            }),
            { total: 0, paid: 0, pending: 0 },
          );

          const totalFreight = data.total_freight || 0;
          const pendingFreight = data.freight_pending_for_billing || 0;
          const completedFreight = Math.max(totalFreight - pendingFreight, 0);

          setStats({
            total_freight: totalFreight,
            total_billing: data.total_billing || 0,
            today_freight: data.today_freight || 0,
            today_billing: data.today_billing || 0,
            current_month_freight: data.current_month_freight || 0,
            current_month_billing: data.current_month_billing || 0,
            freight_pending_for_billing: pendingFreight,
            billing_pending_for_payment: data.billing_pending_for_payment || 0,
            total_amount: totals.total,
            received_amount: totals.paid,
            pending_amount: totals.pending,
            completed_freight: completedFreight,
            cancelled_freight: 0,
            current_month_pending: data.current_month_pending || 0,
            current_month_completed: data.current_month_completed || 0,
          });
          setMonthlyOverview(data.monthly_overview || []);
          setCurrentMonthName(data.current_month_name || "");
        }
      } catch {
        console.error("Error fetching dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const getInitials = (s) =>
    s
      ?.split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) ?? "";

  const getRefNo = (item) =>
    `PRL/${item.billing_id}/${getInitials(item.party_name)}`;

  const getBranchCodeStatic = (b) =>
    b === "RAIPUR" ? "SRE" : b === "BILASPUR" ? "JHT" : "PRL";

  const handleViewClick = async (id) => {
    try {
      const d = await billFreightList(id);
      if (d) {
        setRawBillData({
          isCustom: d.is_custom_bill || false,
          freights: d.freights || [],
          customItems: d.custom_items || [],
          billInfo: d.is_custom_bill
            ? {
                invoice_no: d.invoice_no,
                eway_bill_no: d.eway_bill_no,
                invoice_date: d.invoice_date,
                motor_vehicle_no: d.motor_vehicle_no,
                destination: d.destination,
                dispatched_through: d.dispatched_through,
                party_name: d.party_name || d.party_text,
                total_taxable_value: d.total_taxable_value,
                cgst_percent: d.cgst_percent,
                cgst_amount: d.cgst_amount,
                sgst_percent: d.sgst_percent,
                sgst_amount: d.sgst_amount,
                grand_total: d.grand_total,
              }
            : null,
        });
        setViewModalOpen(true);
      }
    } catch {
      console.error("Error fetching bill details");
    }
  };

  const handlePartyToggle = (partyKey) => {
    setExpandedPartyId((prev) => (prev === partyKey ? null : partyKey));
    setExpandedCardId(null);
  };

  const handleEditClick = (item) => {
    setSelectedBill(item);
    setEditData({
      paid_amount: item.paid_amount || "",
      tds_percent: item.tds_percent ?? (item.party_tds_percent || 0),
      tds_amount: item.tds_amount || "",
      deduction_amount: item.deduction_amount || "",
      deduction_remarks: item.deduction_remarks || "",
      follow_date: item.follow_date || "",
      remarks: item.remarks || "",
      payment_remarks: item.payment_remarks || "",
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBill) return;

    // ── Calculate Net Payable (Total − TDS − Deduction) ──
    const total = Number(selectedBill.total_amount) || 0;
    const tds = Number(editData.tds_amount) || 0;
    const deduction = Number(editData.deduction_amount) || 0;
    const netPayable = total - tds - deduction;
    const paidAmount = Number(editData.paid_amount) || 0;

    // ── Guard: Prevent overpayment ──
    if (paidAmount > netPayable) {
      alert(
        `Received amount (${formatCurrency(paidAmount)}) cannot exceed Net Payable (${formatCurrency(netPayable)}).`,
      );
      return;
    }
    try {
      await billUpdate(selectedBill.billing_id, {
        paid_amount: Number(editData.paid_amount) || 0,
        tds_percent: Number(editData.tds_percent) || 0,
        tds_amount: Number(editData.tds_amount) || 0,
        deduction_amount: Number(editData.deduction_amount) || 0,
        deduction_remarks: editData.deduction_remarks,
        follow_date: editData.follow_date || null,
        remarks: editData.remarks,
        payment_remarks: editData.payment_remarks,
      });
      const d = await dashboardApi();
      if (d) {
        const flattenedBills = [];
        d.billing_data?.forEach((party) => {
          party.pending_bills?.forEach((bill) => {
            flattenedBills.push({
              ...bill,
              party_name: party.name,
              party_id: party.party_id,
              branch_name: party.branch_name || "RAIPUR",
            });
          });
        });

        setBill(flattenedBills);

        const totals = flattenedBills.reduce(
          (acc, b) => ({
            total: acc.total + (Number(b.total_amount) || 0),
            paid: acc.paid + (Number(b.paid_amount) || 0),
            pending: acc.pending + (Number(b.rem_amount) || 0),
          }),
          { total: 0, paid: 0, pending: 0 },
        );

        setStats((prev) => ({
          ...prev,
          total_amount: totals.total,
          received_amount: totals.paid,
          pending_amount: totals.pending,
        }));
      }

      setEditModalOpen(false);
      setSelectedBill(null);
    } catch (err) {
      console.error("Error updating bill", err);
    }
  };

  const handleSettleConfirm = async () => {
    if (!settleId) return;
    try {
      await billSettlement(settleId);
      setBill((prev) => prev.filter((b) => b.billing_id !== settleId));
    } catch (err) {
      console.error("Error settling bill", err);
    } finally {
      setSettleAnchorEl(null);
      setSettleId(null);
    }
  };

  const statCards = [
    {
      title: "Total LR",
      value: stats.total_freight,
      icon: <IconTruck />,
      iconBg: "#FEF2F2",
      iconColor: "#EF4444",
      onClick: () => navigate("/dashboard/Allfreight"),
    },
    {
      title: "Today LR",
      value: stats.today_freight,
      icon: <IconTruckDelivery />,
      iconBg: "#ECFDF5",
      iconColor: "#10B981",
      onClick: () => navigate("/dashboard/freight-list?date_filter=today"),
    },
    {
      title: "Monthly LR",
      value: stats.current_month_freight,
      icon: <IconCalendarStats />,
      iconBg: "#EFF6FF",
      iconColor: "#3B82F6",
      onClick: () => navigate("/dashboard/freight-list?date_filter=month"),
    },
    {
      title: "Total Bills",
      value: stats.total_billing,
      icon: <IconReceipt />,
      iconBg: "#F5F3FF",
      iconColor: "#8B5CF6",
      onClick: () => navigate("/dashboard/Allbills"),
    },
    {
      title: "Pending Billing",
      value: stats.freight_pending_for_billing,
      icon: <IconFileInvoice />,
      iconBg: "#FFF7ED",
      iconColor: "#F97316",
      onClick: () => navigate("/dashboard/bill-creation"),
    },
    {
      title: "Pending Payment",
      value: stats.billing_pending_for_payment,
      icon: <IconCashOff />,
      iconBg: "#FCE7F3",
      iconColor: "#EC4899",
      onClick: () => navigate("/dashboard/pendingforbill"),
    },
  ];

  const groupedBills = (() => {
    const map = new Map();

    bill.forEach((item) => {
      const key = item.party_id ?? item.party_name;

      if (!map.has(key)) {
        map.set(key, {
          party_id: item.party_id,
          party_name: item.party_name,
          branch_name: item.branch_name,
          bills: [],
        });
      }

      map.get(key).bills.push(item);
    });

    return Array.from(map.values());
  })();

  if (loading) {
    return (
      <Box sx={{ px: { xs: 0, md: 2.5 }, pt: 2, width: "100%" }}>
        <LinearProgress
          sx={{
            borderRadius: 1,
            bgcolor: "#eef2ff",
            "& .MuiLinearProgress-bar": { bgcolor: brandColor },
          }}
        />
        <Typography
          sx={{
            mt: 2,
            textAlign: "center",
            color: "#64748B",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 2.5 },
        pt: { xs: 2, md: 2 },
        pb: { xs: 2, md: 3 },
        maxWidth: "100%",
        width: "100%",
      }}
    >
      {/* Row 1: Stat Cards - 6 across (top 6 only) */}
      <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
        {statCards.slice(0, 6).map((c, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <StatCard {...c} />
          </Grid>
        ))}
      </Grid>

      {/* Row 2: Business Overview + Summary + LR Status */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <BusinessOverviewChart
            monthlyData={monthlyOverview}
            monthName={currentMonthName}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SummaryCard stats={stats} />
        </Grid>
        <Grid item xs={12} md={4}>
          <LRStatusDonut stats={stats} />
        </Grid>
      </Grid>

      {/* Row 3: Recent Bills Overview Header */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #F1F5F9",
          borderRadius: "12px 12px 0 0",
          borderBottom: "none",
          px: 2,
          py: 1.25,
          bgcolor: "#FFFFFF",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "6px",
                bgcolor: "#FEE2E2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReceiptIcon sx={{ fontSize: 16, color: "#DC2626" }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#0F172A" }}
            >
              Recent Bills Overview
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {isMobile ? (
        <Box sx={{ mt: 0.5 }}>
          {bill.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <ReceiptIcon sx={{ fontSize: 48, color: "#c7d2fe", mb: 1 }} />
              <Typography
                sx={{ fontWeight: 600, color: "#666", fontSize: "0.8rem" }}
              >
                No bills found
              </Typography>
            </Box>
          ) : (
            <>
              {/* Scrollable party list */}
              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                {groupedBills.map((group, gIdx) => {
                  const partyKey = group.party_id ?? group.party_name;
                  const isOpen = expandedPartyId === partyKey;
                  const partyTotal = group.bills.reduce(
                    (s, c) => s + (Number(c.total_amount) || 0),
                    0,
                  );
                  const partyPaid = group.bills.reduce(
                    (s, c) => s + (Number(c.paid_amount) || 0),
                    0,
                  );
                  const partyRem = group.bills.reduce(
                    (s, c) => s + (Number(c.rem_amount) || 0),
                    0,
                  );

                  return (
                    <Box key={partyKey} sx={{ mb: 1 }}>
                      {/* Party header — clickable */}
                      <Box
                        onClick={() => handlePartyToggle(partyKey)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.25,
                          py: 0.9,
                          bgcolor: isOpen ? brandColor : "#eef2ff",
                          borderRadius: isOpen ? "6px 6px 0 0" : "6px",
                          cursor: "pointer",
                          transition: "background-color 0.15s",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.55rem",
                              fontWeight: 800,
                              color: isOpen ? "#fff" : brandColor,
                              bgcolor: isOpen
                                ? "rgba(255,255,255,0.2)"
                                : "#fff",
                              px: 0.6,
                              borderRadius: "4px",
                            }}
                          >
                            #{gIdx + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 700,
                              color: isOpen ? "#fff" : brandColor,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {group.party_name}
                          </Typography>
                          <Chip
                            label={`${group.bills.length} Bill${group.bills.length > 1 ? "s" : ""}`}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.5rem",
                              fontWeight: 700,
                              bgcolor: isOpen
                                ? "rgba(255,255,255,0.2)"
                                : "#fff",
                              color: isOpen ? "#fff" : brandColor,
                              "& .MuiChip-label": { px: 0.6 },
                            }}
                          />
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            color: isOpen ? "#fff" : brandColor,
                            transform: isOpen
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        >
                          ▾
                        </Typography>
                      </Box>

                      {/* Bills — only rendered when this party is open */}
                      {isOpen && (
                        <Box
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderTop: "none",
                            p: 0.75,
                          }}
                        >
                          {group.bills.map((item, idx) => (
                            <MobileBillCard
                              key={item.billing_id}
                              item={item}
                              idx={idx}
                              expanded={expandedCardId === item.billing_id}
                              onToggle={() => handleCardToggle(item.billing_id)}
                              onView={handleViewClick}
                              onEdit={handleEditClick}
                              onSettle={(e, id) => {
                                setSettleAnchorEl(e.currentTarget);
                                setSettleId(id);
                              }}
                              getBranchCode={getRefNo}
                            />
                          ))}
                          {group.bills.length > 1 && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                px: 1,
                                py: 0.6,
                                mt: 0.5,
                                bgcolor: "#FFFBEB",
                                border: "1px solid #fde68a",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  color: "#b45309",
                                }}
                              >
                                Party Total
                              </Typography>
                              <Stack direction="row" spacing={1.25}>
                                <Typography
                                  sx={{ fontSize: "0.6rem", fontWeight: 700 }}
                                >
                                  {formatCurrency(partyTotal)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    color: "#10b981",
                                  }}
                                >
                                  {formatCurrency(partyPaid)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    color: "#dc2626",
                                  }}
                                >
                                  {formatCurrency(partyRem)}
                                </Typography>
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Footer — normal flex item, always visible, never sticky/clipped */}
              <Paper
                sx={{
                  mt: 1,
                  mb: 8,
                  borderRadius: "0px",
                  border: "1px solid #e0e0e0",
                  borderTop: `2px solid ${brandColor}`,
                  boxShadow: "0 -4px 10px rgba(0,0,0,0.04)",
                  bgcolor: "#eef2ff",
                }}
              >
                <Box sx={{ px: 1.25, py: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: brandColor,
                      mb: 0.75,
                    }}
                  >
                    Totals ({bill.length} Bills)
                  </Typography>
                  <Grid container spacing={0.5}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 0.75,
                          bgcolor: "#fff",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.55rem", color: "#6b7280" }}
                        >
                          Total
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 700 }}
                        >
                          {formatCurrency(
                            bill.reduce(
                              (s, c) => s + (Number(c.total_amount) || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 0.75,
                          bgcolor: "#fff",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.55rem", color: "#6b7280" }}
                        >
                          Received
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(
                            bill.reduce(
                              (s, c) => s + (Number(c.paid_amount) || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 0.75,
                          bgcolor: "#fff5f5",
                          border: "1px solid #fecaca",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.55rem", color: "#6b7280" }}
                        >
                          Balance
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "#dc2626",
                          }}
                        >
                          {formatCurrency(
                            bill.reduce(
                              (s, c) => s + (Number(c.rem_amount) || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 0.75,
                          bgcolor: "#eef2ff",
                          border: "1px solid #c7d2fe",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.55rem", color: "#6b7280" }}
                        >
                          Bills
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: brandColor,
                          }}
                        >
                          {bill.length}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </>
          )}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "0 0 12px 12px",
            border: `1px solid #F1F5F9`,
            borderTop: "none",
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
          }}
        >
          <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                borderCollapse: "collapse",
                "& td, & th": {
                  py: 0.4,
                  px: 1,
                  fontSize: "0.75rem",
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  lineHeight: 1.3,
                  color: T.text,
                },
                "& th": {
                  fontWeight: 600,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  py: 0.6,
                  height: 36,
                  fontSize: "0.72rem",
                  letterSpacing: "0.02em",
                  textTransform: "none",
                  borderBottom: `1px solid ${T.border}`,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    { l: "#", sticky: true, left: 0, w: 35 },
                    { l: "Party", sticky: true, left: 35, w: 130 },
                    { l: "Bill ID", w: 100 },
                    { l: "Date", w: 85 },
                    { l: "Days", w: 60 },
                    { l: "Total", w: 80 },
                    { l: "TDS %", w: 55 },
                    { l: "TDS Amt", w: 80 },
                    { l: "Deduction", w: 85 },
                    { l: "Net Payable", w: 90 },
                    { l: "Received", w: 80 },
                    { l: "Balance", w: 80 },
                    { l: "Follow Up", w: 85 },
                    { l: "Remarks", w: 120 },
                    { l: "Pay Remarks", w: 120 },
                    { l: "Actions", sticky: true, right: 0, w: 75 },
                  ].map(({ l, sticky, left, right, w }) => (
                    <TableCell
                      key={l}
                      sx={{
                        minWidth: w,
                        ...(sticky && {
                          position: "sticky",
                          ...(left !== undefined && { left }),
                          ...(right !== undefined && { right }),
                          zIndex: 12,
                          bgcolor: `${T.headerBg} !important`,
                        }),
                      }}
                    >
                      {l}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {groupedBills.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={16}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <ReceiptIcon
                        sx={{
                          fontSize: 40,
                          color: T.border,
                          mb: 1,
                          display: "block",
                          mx: "auto",
                        }}
                      />
                      <Typography
                        sx={{
                          color: T.textSub,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        No bills found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupedBills.flatMap((group, groupIdx) => {
                    const borderStyle = `1px solid ${T.border}`;
                    const partyTotal = group.bills.reduce(
                      (s, c) => s + (Number(c.total_amount) || 0),
                      0,
                    );
                    const partyPaid = group.bills.reduce(
                      (s, c) => s + (Number(c.paid_amount) || 0),
                      0,
                    );
                    const partyRem = group.bills.reduce(
                      (s, c) => s + (Number(c.rem_amount) || 0),
                      0,
                    );

                    const showSubtotal = true;
                    const mergeSpan = group.bills.length + 1;

                    const billRows = group.bills.map((item, billIdx) => {
                      const isFirst = billIdx === 0;
                      const isLast = billIdx === group.bills.length - 1;
                      const rowBg = T.white; // Flat white like reference
                      const sc = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      };
                      const daysDiff = Math.ceil(
                        (new Date() - new Date(item.created_at)) /
                          (1000 * 60 * 60 * 24),
                      );

                      return (
                        <TableRow
                          key={`${group.party_id}-${item.billing_id}`}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                              borderBottom:
                                isLast && !showSubtotal
                                  ? `1.5px solid ${T.primaryMid}`
                                  : `1px solid ${T.border}`,
                            },
                            "&:hover > td": {
                              bgcolor: `${T.hoverRow} !important`,
                            },
                          }}
                        >
                          {/* SR NO — merged */}
                          {isFirst && (
                            <TableCell
                              rowSpan={mergeSpan}
                              sx={{
                                position: "sticky",
                                left: 0,
                                ...sc,
                                verticalAlign: "middle",
                                textAlign: "center",
                                borderBottom: `1.5px solid ${T.primaryMid} !important`,
                                borderRight: borderStyle,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  color: T.textMuted,
                                }}
                              >
                                {groupIdx + 1}
                              </Typography>
                            </TableCell>
                          )}

                          {/* PARTY — merged */}
                          {isFirst && (
                            <TableCell
                              rowSpan={mergeSpan}
                              sx={{
                                position: "sticky",
                                left: 35,
                                ...sc,
                                verticalAlign: "middle",
                                borderBottom: `1.5px solid ${T.primaryMid} !important`,
                                borderRight: borderStyle,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: T.primary,
                                  fontSize: "0.72rem",
                                  maxWidth: 130,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {group.party_name}
                              </Typography>
                            </TableCell>
                          )}

                          {/* BILL ID */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                              <Chip
                                label={getRefNo(item)}
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: 20,
                                  borderRadius: "6px !important",
                                  bgcolor: item.is_custom_bill
                                    ? "#DBEAFE"
                                    : "#FEE2E2",
                                  color: item.is_custom_bill
                                    ? "#1E40AF"
                                    : "#DC2626",
                                  border: `1px solid ${item.is_custom_bill ? "#BFDBFE" : "#FECACA"}`,
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 0.75 },
                                }}
                              />
                          </TableCell>

                          {/* DATE */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            {formatDate(item.created_at)}
                          </TableCell>

                          {/* DAYS */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            <Typography
                              sx={{
                                fontWeight: daysDiff > 30 ? 700 : 500,
                                color: daysDiff > 30 ? T.red : T.textSub,
                                fontSize: "0.68rem",
                              }}
                            >
                              {daysDiff}d
                            </Typography>
                          </TableCell>

                          {/* TOTAL */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            {formatCurrency(item.total_amount)}
                          </TableCell>

                          <TableCell sx={{ borderRight: borderStyle }}>
                            {item.tds_percent > 0 ? (
                              <Chip
                                label={`${item.tds_percent || 0}%`}
                                size="small"
                                sx={{
                                  fontSize: "0.55rem",
                                  height: 16,
                                  borderRadius: "6px !important",
                                  bgcolor:
                                    item.tds_percent > 0
                                      ? "#FEE2E2"
                                      : "#F1F5F9",
                                  color:
                                    item.tds_percent > 0
                                      ? "#DC2626"
                                      : "#64748B",
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 0.5, py: 0 },
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{ fontSize: "0.65rem", color: T.textMuted }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* ── NEW: TDS Amount ── */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            {item.tds_amount > 0 ? (
                              <Typography
                                sx={{
                                  color: "#DC2626",
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                }}
                              >
                                {formatCurrency(item.tds_amount)}
                              </Typography>
                            ) : (
                              "—"
                            )}
                          </TableCell>

                          {/* ── NEW: Deduction ── */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            {item.deduction_amount > 0 ? (
                              <Tooltip
                                title={item.deduction_remarks || ""}
                                arrow
                              >
                                <Typography
                                  sx={{
                                    color: "#EA580C",
                                    fontWeight: 600,
                                    fontSize: "0.72rem",
                                  }}
                                >
                                  {formatCurrency(item.deduction_amount)}
                                </Typography>
                              </Tooltip>
                            ) : (
                              "—"
                            )}
                          </TableCell>

                          {/* ── NEW: Net Payable ── */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#7C3AED",
                                fontSize: "0.72rem",
                              }}
                            >
                              {formatCurrency(
                                (Number(item.total_amount) || 0) -
                                  (Number(item.tds_amount) || 0) -
                                  (Number(item.deduction_amount) || 0),
                              )}
                            </Typography>
                          </TableCell>

                          {/* RECEIVED */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            <Typography
                              sx={{
                                color: T.success,
                                fontWeight: 600,
                                fontSize: "0.72rem",
                              }}
                            >
                              {formatCurrency(item.paid_amount)}
                            </Typography>
                          </TableCell>

                          {/* BALANCE */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: T.red,
                                fontSize: "0.72rem",
                              }}
                            >
                              {formatCurrency(item.rem_amount)}
                            </Typography>
                          </TableCell>

                          {/* FOLLOW UP */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            {item.follow_date
                              ? formatDate(item.follow_date)
                              : "—"}
                          </TableCell>

                          {/* REMARKS */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            <Tooltip title={item.remarks || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.remarks || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* PAYMENT REMARKS */}
                          <TableCell sx={{ borderRight: borderStyle }}>
                            <Tooltip title={item.payment_remarks || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.payment_remarks || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* ACTIONS — per row, NOT merged */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...sc,
                              borderBottom:
                                isLast && !showSubtotal
                                  ? `1.5px solid ${T.primaryMid} !important`
                                  : `1px solid ${T.border}`,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                              alignItems="center"
                            >
                              <ActionButton
                                tip="View"
                                icon={<Visibility />}
                                color="#8b5cf6"
                                onClick={() => handleViewClick(item.billing_id)}
                              />
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                color={T.primary}
                                onClick={() => handleEditClick(item)}
                              />
                              {/* ← add */}
                              <Tooltip title="Settle" arrow placement="top">
                                <Checkbox
                                  size="small"
                                  onClick={(e) => {
                                    setSettleAnchorEl(e.currentTarget);
                                    setSettleId(item.billing_id);
                                  }}
                                  icon={
                                    <CheckCircle
                                      sx={{ fontSize: 18, color: "#d1d5db" }}
                                    />
                                  }
                                  checkedIcon={
                                    <CheckCircle
                                      sx={{ fontSize: 18, color: "#10b981" }}
                                    />
                                  }
                                  sx={{ p: 0.4 }}
                                />
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    });

                    // Subtotal row — only if party has more than 1 bill
                    const subtotalRow = showSubtotal ? (
                      <TableRow
                        key={`subtotal-${group.party_id}`}
                        sx={{
                          "& > td": {
                            bgcolor: "#FFFBEB !important",
                            borderBottom: `1px solid ${T.border}`,
                            py: 0.6,
                            fontSize: "0.72rem",
                            fontWeight: 700,
                          },
                          "&:hover > td": {
                            bgcolor: "#FEF3C7 !important",
                          },
                        }}
                      >
                        <TableCell
                          colSpan={3}
                          sx={{
                            textAlign: "right",
                            color: T.warning,
                            fontWeight: 800,
                            fontSize: "0.68rem",
                            borderRight: borderStyle,
                            pr: 1,
                            position: "sticky",
                            left: 0,
                            bgcolor: "#FFFBEB !important",
                            zIndex: 5,
                          }}
                        >
                          Party Total →
                        </TableCell>

                        {/* Total */}
                        <TableCell
                          sx={{ borderRight: borderStyle, fontWeight: 800 }}
                        >
                          {formatCurrency(partyTotal)}
                        </TableCell>

                        {/* TDS % — blank in subtotal */}
                        <TableCell sx={{ borderRight: borderStyle }} />

                        {/* TDS Amt */}
                        <TableCell
                          sx={{
                            borderRight: borderStyle,
                            color: "#DC2626",
                            fontWeight: 700,
                          }}
                        >
                          {formatCurrency(
                            group.bills.reduce(
                              (s, c) => s + (Number(c.tds_amount) || 0),
                              0,
                            ),
                          )}
                        </TableCell>

                        {/* Deduction */}
                        <TableCell
                          sx={{
                            borderRight: borderStyle,
                            color: "#EA580C",
                            fontWeight: 700,
                          }}
                        >
                          {formatCurrency(
                            group.bills.reduce(
                              (s, c) => s + (Number(c.deduction_amount) || 0),
                              0,
                            ),
                          )}
                        </TableCell>

                        {/* Net Payable */}
                        <TableCell
                          sx={{
                            borderRight: borderStyle,
                            color: "#7C3AED",
                            fontWeight: 700,
                          }}
                        >
                          {formatCurrency(
                            group.bills.reduce(
                              (s, c) =>
                                s +
                                ((Number(c.total_amount) || 0) -
                                  (Number(c.tds_amount) || 0) -
                                  (Number(c.deduction_amount) || 0)),
                              0,
                            ),
                          )}
                        </TableCell>

                        {/* Paid */}
                        <TableCell
                          sx={{
                            borderRight: borderStyle,
                            color: T.success,
                            fontWeight: 800,
                          }}
                        >
                          {formatCurrency(partyPaid)}
                        </TableCell>

                        {/* Balance */}
                        <TableCell
                          sx={{
                            borderRight: borderStyle,
                            color: T.red,
                            fontWeight: 800,
                          }}
                        >
                          {formatCurrency(partyRem)}
                        </TableCell>

                        {/* Follow, Remarks, PayRemarks — blank */}
                        <TableCell
                          colSpan={3}
                          sx={{ borderRight: borderStyle }}
                        />

                        {/* Actions sticky */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            bgcolor: "#FFFBEB !important",
                            borderBottom: `1px solid ${T.border}`,
                          }}
                        />
                      </TableRow>
                    ) : null;

                    return [...billRows, ...(subtotalRow ? [subtotalRow] : [])];
                  })
                )}

                {/* Totals Row */}
                {bill.length > 0 && (
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 20,
                      "& td": {
                        bgcolor: "#F9FAFB",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        py: 0.75,
                        borderTop: `1px solid ${T.border}`,
                        borderBottom: "none",
                        color: T.text,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 22,
                        bgcolor: "#F9FAFB !important",
                      }}
                    />

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 35,
                        zIndex: 22,
                        color: T.text,
                        fontWeight: 800,
                        bgcolor: "#F9FAFB !important",
                      }}
                    >
                      Totals
                    </TableCell>

                    {/* Bill ID, Date, Days */}
                    <TableCell colSpan={3} />

                    {/* Total */}
                    <TableCell sx={{ fontWeight: 800 }}>
                      {formatCurrency(
                        bill.reduce(
                          (sum, item) => sum + (Number(item.total_amount) || 0),
                          0,
                        ),
                      )}
                    </TableCell>

                    <TableCell sx={{ color: T.textMuted, fontSize: "0.68rem" }}>
                      —
                    </TableCell>

                    {/* TDS Amount */}
                    <TableCell sx={{ color: "#DC2626", fontWeight: 800 }}>
                      {formatCurrency(
                        bill.reduce(
                          (sum, item) => sum + (Number(item.tds_amount) || 0),
                          0,
                        ),
                      )}
                    </TableCell>

                    {/* Deduction */}
                    <TableCell sx={{ color: "#EA580C", fontWeight: 800 }}>
                      {formatCurrency(
                        bill.reduce(
                          (sum, item) =>
                            sum + (Number(item.deduction_amount) || 0),
                          0,
                        ),
                      )}
                    </TableCell>

                    {/* Net Payable */}
                    <TableCell sx={{ color: "#7C3AED", fontWeight: 800 }}>
                      {formatCurrency(
                        bill.reduce(
                          (sum, item) =>
                            sum +
                            (Number(item.total_amount) || 0) -
                            (Number(item.tds_amount) || 0) -
                            (Number(item.deduction_amount) || 0),
                          0,
                        ),
                      )}
                    </TableCell>

                    {/* Received */}
                    <TableCell sx={{ color: T.success, fontWeight: 800 }}>
                      {formatCurrency(
                        bill.reduce(
                          (sum, item) => sum + (Number(item.paid_amount) || 0),
                          0,
                        ),
                      )}
                    </TableCell>

                    {/* Balance */}
                    <TableCell sx={{ color: T.red, fontWeight: 800 }}>
                      {formatCurrency(
                        bill.reduce(
                          (sum, item) => sum + (Number(item.rem_amount) || 0),
                          0,
                        ),
                      )}
                    </TableCell>

                    {/* Follow Up, Remarks, Payment Remarks */}
                    <TableCell colSpan={3} />

                    {/* Actions */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 21,
                        bgcolor: "#F9FAFB !important",
                      }}
                    />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {/* Edit Dialog - Keep as is */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "0px",
            border: "1px solid #e0e0e0",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
            px: 3,
            py: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{ fontWeight: 700, color: "#fff", fontSize: "1rem" }}
              >
                Edit {selectedBill?.is_custom_bill ? "Custom Bill" : "Bill"}
              </Typography>
              {selectedBill?.is_custom_bill && (
                <Chip
                  label="CUSTOM"
                  size="small"
                  sx={{
                    bgcolor: "#fff",
                    color: "#333",
                    fontSize: "0.55rem",
                    fontWeight: 800,
                    height: 16,
                    borderRadius: "3px",
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              )}
            </Stack>
            <Chip
              label={selectedBill ? getRefNo(selectedBill) : ""}
              size="small"
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.93)",
                color: "#111",
                borderRadius: "4px",
              }}
            />
          </Stack>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            {/* ── Rec Amt ── */}
            <Box>
              {/* Mini summary strip — moved above */}
              {selectedBill && (
                <Stack
                  direction="row"
                  spacing={0}
                  sx={{ mb: 0.75, border: "1px solid #e0e0e0" }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      px: 1,
                      py: 0.5,
                      bgcolor: "#f8faff",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.58rem", color: "#6b7280" }}>
                      Net Payable
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>
                      {formatCurrency(selectedBill.net_payable)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      px: 1,
                      py: 0.5,
                      bgcolor: "#f0fdf4",
                      borderRight: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.58rem", color: "#6b7280" }}>
                      Prev. Rec
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#10b981",
                      }}
                    >
                      {formatCurrency(selectedBill.paid_amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, px: 1, py: 0.5, bgcolor: "#fff5f5" }}>
                    <Typography sx={{ fontSize: "0.58rem", color: "#6b7280" }}>
                      Prev. Bal
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#dc2626",
                      }}
                    >
                      {formatCurrency(selectedBill.rem_amount)}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#374151",
                  mb: 0.5,
                }}
              >
                Rec Amt
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter received amount"
                type="number"
                size="small"
                value={editData.paid_amount}
                onChange={(e) => {
                  const value = e.target.value;
                  const total = Number(selectedBill?.total_amount) || 0;
                  const tds = Number(editData.tds_amount) || 0;
                  const deduction = Number(editData.deduction_amount) || 0;
                  const netPayable = total - tds - deduction;

                  // Prevent typing more than net payable
                  if (Number(value) > netPayable) {
                    setEditData((p) => ({
                      ...p,
                      paid_amount: netPayable.toString(),
                    }));
                  } else {
                    setEditData((p) => ({ ...p, paid_amount: value }));
                  }
                }}
                inputProps={{
                  min: 0,
                  max:
                    (Number(selectedBill?.total_amount) || 0) -
                    (Number(editData.tds_amount) || 0) -
                    (Number(editData.deduction_amount) || 0),
                }}
                helperText={(() => {
                  const total = Number(selectedBill?.total_amount) || 0;
                  const tds = Number(editData.tds_amount) || 0;
                  const deduction = Number(editData.deduction_amount) || 0;
                  const netPayable = total - tds - deduction;
                  const paid = Number(editData.paid_amount) || 0;
                  const balance = netPayable - paid;

                  if (balance === 0 && paid > 0) {
                    return `✓ Fully settled`;
                  }
                })()}
                FormHelperTextProps={{
                  sx: {
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    mx: 0,
                    mt: 0.5,
                    color:
                      Number(editData.paid_amount) ===
                      (Number(selectedBill?.total_amount) || 0) -
                        (Number(editData.tds_amount) || 0) -
                        (Number(editData.deduction_amount) || 0)
                        ? "#10b981"
                        : "#6b7280",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    bgcolor: "#f0fdf4",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#10b981" },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]": { MozAppearance: "textfield" },
                }}
              />
            </Box>
            {/* ── TDS SECTION ── */}
            <Box
              sx={{ border: "1px solid #FECACA", bgcolor: "#FEF2F2", p: 1.25 }}
            >
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "#DC2626",
                  mb: 1,
                }}
              >
                TDS Deduction
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {/* TDS % */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#374151",
                      mb: 0.5,
                    }}
                  >
                    TDS %
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    placeholder="e.g. 2"
                    value={editData.tds_percent}
                    onChange={(e) => {
                      const pct = e.target.value;
                      const total = Number(selectedBill?.total_amount) || 0;
                      // Auto-recalculate tds_amount when % changes
                      const auto = parseFloat(
                        ((total * Number(pct)) / 100).toFixed(2),
                      );
                      setEditData((p) => ({
                        ...p,
                        tds_percent: pct,
                        tds_amount: auto,
                      }));
                    }}
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px",
                        bgcolor: "#fff",
                        "& fieldset": { border: "none" },
                      },
                    }}
                  />
                </Box>

                {/* TDS Amount — auto-filled, manually editable */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#374151",
                      mb: 0.5,
                    }}
                  >
                    TDS Amt ₹
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    placeholder="Auto"
                    value={editData.tds_amount}
                    onChange={(e) =>
                      setEditData((p) => ({ ...p, tds_amount: e.target.value }))
                    }
                    inputProps={{ min: 0 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px",
                        bgcolor: "#fff",
                        "& fieldset": { border: "none" },
                      },
                      "& .MuiInputBase-input": {
                        color: "#DC2626",
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Box>

            {/* ── DEDUCTION SECTION ── */}
            <Box
              sx={{ border: "1px solid #FED7AA", bgcolor: "#FFF7ED", p: 1.25 }}
            >
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "#EA580C",
                  mb: 1,
                }}
              >
                Deduction Charges
              </Typography>

              <TextField
                fullWidth
                label="Deduction Amount (₹)"
                type="number"
                size="small"
                value={editData.deduction_amount}
                onChange={(e) =>
                  setEditData((p) => ({
                    ...p,
                    deduction_amount: e.target.value,
                  }))
                }
                inputProps={{ min: 0 }}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    bgcolor: "#fff",
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input": {
                    color: "#EA580C",
                    fontWeight: 700,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Deduction Reason"
                size="small"
                placeholder="e.g. Short delivery, damage..."
                value={editData.deduction_remarks}
                onChange={(e) =>
                  setEditData((p) => ({
                    ...p,
                    deduction_remarks: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    bgcolor: "#fff",
                    "& fieldset": { border: "none" },
                  },
                }}
              />
            </Box>

            {/* ── Follow Date ── */}
            <TextField
              fullWidth
              label="Follow Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={editData.follow_date}
              onChange={(e) =>
                setEditData((p) => ({ ...p, follow_date: e.target.value }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  bgcolor: "#f8faff",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: brandColor },
              }}
            />

            {/* ── Remarks ── */}
            <TextField
              fullWidth
              label="Remarks"
              multiline
              rows={2}
              size="small"
              value={editData.remarks}
              onChange={(e) =>
                setEditData((p) => ({ ...p, remarks: e.target.value }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  bgcolor: "#f8faff",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: brandColor },
              }}
            />

            {/* ── Payment Remarks ── */}
            <TextField
              fullWidth
              label="Payment Remarks"
              multiline
              rows={2}
              size="small"
              value={editData.payment_remarks}
              onChange={(e) =>
                setEditData((p) => ({
                  ...p,
                  payment_remarks: e.target.value,
                }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  bgcolor: "#f8faff",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiInputLabel-root.Mui-focused": { color: brandColor },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, gap: 1, bgcolor: "#f8fafc" }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            sx={{ borderRadius: "0px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: "0px",
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              color: "#fff",
              boxShadow: "none",
              "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Dialog - Updated with FreightList table style */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 12px 40px rgba(30,41,59,0.18)",
            overflow: "hidden",
          },
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
            px: 3,
            py: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                sx={{ fontWeight: 700, color: "#fff", fontSize: "1rem" }}
              >
                {rawBillData.isCustom ? "Custom Bill Details" : "LR Details"}
              </Typography>
              {rawBillData.isCustom
                ? rawBillData.customItems.length > 0 && (
                    <Chip
                      label={`${rawBillData.customItems.length} Item${rawBillData.customItems.length > 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        bgcolor: "#FFFFFF",
                        color: "#4338CA",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        height: 22,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  )
                : rawBillData.freights.length > 0 && (
                    <Chip
                      label={`${rawBillData.freights.length} LR${rawBillData.freights.length > 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        bgcolor: "#FFFFFF",
                        color: "#4338CA",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        height: 22,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  )}
            </Stack>
            <IconButton
              onClick={() => setViewModalOpen(false)}
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {rawBillData.isCustom ? (
            // ═══════════════════════════════════════════════
            // ── CUSTOM BILL VIEW ──
            // ═══════════════════════════════════════════════
            <>
              {/* Bill Info Header */}
              {rawBillData.billInfo && (
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    bgcolor: "#F0F9FF",
                    borderBottom: "1px solid #BAE6FD",
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Party Name — takes more space (5 columns) */}
                    <Grid item xs={12} sm={4}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#64748B",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Party Name
                      </Typography>
                      <Tooltip
                        title={rawBillData.billInfo.party_name || "—"}
                        arrow
                      >
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#0F172A",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {rawBillData.billInfo.party_name || "—"}
                        </Typography>
                      </Tooltip>
                    </Grid>

                    {/* Invoice No */}
                    <Grid item xs={6} sm={2}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#64748B",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Invoice No
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#0F172A",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rawBillData.billInfo.invoice_no || "—"}
                      </Typography>
                    </Grid>

                    {/* E-way Bill No */}
                    <Grid item xs={6} sm={2}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#64748B",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        E-way Bill No
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#0F172A",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rawBillData.billInfo.eway_bill_no || "—"}
                      </Typography>
                    </Grid>

                    {/* Vehicle No */}
                    <Grid item xs={6} sm={2}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#64748B",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Vehicle No
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#0F172A",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rawBillData.billInfo.motor_vehicle_no || "—"}
                      </Typography>
                    </Grid>

                    {/* Destination */}
                    <Grid item xs={6} sm={2}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#64748B",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Destination
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#0F172A",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rawBillData.billInfo.destination || "—"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Items Table */}
              {rawBillData.customItems.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <ReceiptIcon sx={{ fontSize: 40, color: "#C7D2FE", mb: 1 }} />
                  <Typography
                    sx={{
                      color: "#64748B",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    No items found
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: "calc(90vh - 340px)" }}>
                  <Table
                    stickyHeader
                    size="small"
                    sx={{
                      "& td, & th": {
                        py: 0.5,
                        px: 1,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #E2E8F0",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      },
                      "& th": {
                        fontWeight: 700,
                        color: "#EEF2FF",
                        bgcolor: "#4338CA !important",
                        fontSize: "0.75rem",
                        height: 32,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 40 }}>Sl No.</TableCell>
                        <TableCell
                          sx={{ minWidth: 220, textAlign: "left !important" }}
                        >
                          Description of Goods
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>HSN/SAC</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>Quantity</TableCell>
                        <TableCell sx={{ minWidth: 70 }}>Unit</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>Rate</TableCell>
                        <TableCell sx={{ minWidth: 60 }}>per</TableCell>
                        <TableCell sx={{ minWidth: 110 }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rawBillData.customItems.map((item, idx) => (
                        <TableRow
                          key={item.item_id}
                          sx={{
                            "& > td": {
                              bgcolor: idx % 2 === 0 ? "#F5F7FF" : "#FFFFFF",
                            },
                            "&:hover > td": { bgcolor: "#E0E7FF !important" },
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                color: "#94A3B8",
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "left !important",
                              fontWeight: 600,
                            }}
                          >
                            {item.description || "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.hsn || "—"}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 18,
                                borderRadius: "4px",
                                bgcolor: "#EEF2FF",
                                color: "#4338CA",
                                border: "1px solid #C7D2FE",
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.6 },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {item.quantity}
                          </TableCell>
                          <TableCell>{item.unit || "—"}</TableCell>
                          <TableCell sx={{ color: "#7C3AED", fontWeight: 600 }}>
                            ₹{item.rate}
                          </TableCell>
                          <TableCell>{item.per || "—"}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#059669" }}>
                            {formatCurrency(item.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tax Summary Footer */}
              {rawBillData.billInfo && (
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    bgcolor: "#FFFBEB",
                    borderTop: "2px solid #F59E0B",
                  }}
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#78350F",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        Taxable Value
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#78350F",
                        }}
                      >
                        {formatCurrency(
                          rawBillData.billInfo.total_taxable_value,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#78350F",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        CGST ({rawBillData.billInfo.cgst_percent}%)
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#78350F",
                        }}
                      >
                        {formatCurrency(rawBillData.billInfo.cgst_amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#78350F",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        SGST ({rawBillData.billInfo.sgst_percent}%)
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: "#78350F",
                        }}
                      >
                        {formatCurrency(rawBillData.billInfo.sgst_amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#059669",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        Grand Total
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 800,
                          color: "#059669",
                        }}
                      >
                        {formatCurrency(rawBillData.billInfo.grand_total)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          ) : (
            // ═══════════════════════════════════════════════
            // ── FREIGHT BILL VIEW (EXISTING) ──
            // ═══════════════════════════════════════════════
            <>
              {rawBillData.freights.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <ReceiptIcon sx={{ fontSize: 40, color: "#C7D2FE", mb: 1 }} />
                  <Typography
                    sx={{
                      color: "#64748B",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    No LR details found
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer
                    sx={{ maxHeight: "calc(90vh - 160px)", overflowX: "auto" }}
                  >
                    {/* ═══════════════════════════════════════════════ */}
                    {/* Paste your EXISTING LR Table code HERE           */}
                    {/* Just change: rawBillData.map → rawBillData.freights.map */}
                    {/* ═══════════════════════════════════════════════ */}
                    <Table
                      stickyHeader
                      size="small"
                      sx={{
                        minWidth: 1500,
                        borderCollapse: "collapse",
                        "& td, & th": {
                          py: 0,
                          px: 0.75,
                          fontSize: "0.75rem",
                          borderBottom: "1px solid #E2E8F0",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          lineHeight: 1.2,
                        },
                        "& th": {
                          fontWeight: 700,
                          color: "#EEF2FF",
                          bgcolor: "#4338CA !important",
                          py: 0.2,
                          fontSize: "0.72rem",
                          letterSpacing: "0.01em",
                          borderBottom: "1px solid #4338CA",
                          lineHeight: 1.2,
                          height: 32,
                          whiteSpace: "nowrap",
                        },
                        "& td": { height: 26 },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {[
                            { l: "#", w: 35 },
                            { l: "LR No", w: 80, sticky: true, left: 0 },
                            { l: "Truck No", w: 100 },
                            { l: "Route", w: 150 },
                            { l: "Loading Date", w: 100 },
                            { l: "Unloading Date", w: 100 },
                            { l: "Package", w: 100 },
                            { l: "Description", w: 180 },
                            { l: "Actual Wt", w: 80 },
                            { l: "Charged Wt", w: 85 },
                            { l: "Rate (₹)", w: 80 },
                            { l: "Amount (₹)", w: 90 },
                            { l: "E-way Bill", w: 90 },
                            { l: "Invoice No", w: 90 },
                            { l: "Goods", w: 120 },
                            { l: "Packing", w: 100 },
                            { l: "Subtotal", w: 100, sticky: true, right: 0 },
                          ].map(({ l, w, sticky, left, right }) => (
                            <TableCell
                              key={l}
                              sx={{
                                minWidth: w,
                                bgcolor: "#4338CA !important",
                                color: "#EEF2FF !important",
                                ...(sticky && {
                                  position: "sticky",
                                  ...(left !== undefined && { left }),
                                  ...(right !== undefined && { right }),
                                  zIndex: 12,
                                }),
                              }}
                            >
                              {l}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rawBillData.freights.map((freight, freightIdx) => {
                          const pkgs =
                            freight.package_items &&
                            freight.package_items.length > 0
                              ? freight.package_items
                              : [{}];
                          const rowSpan = pkgs.length;
                          const freightBase =
                            freightIdx % 2 === 0 ? "#F5F7FF" : "#FFFFFF";

                          return pkgs.map((pkg, pkgIdx) => {
                            const isFirst = pkgIdx === 0;
                            const isLast = pkgIdx === pkgs.length - 1;

                            const rowBg =
                              pkgIdx % 2 === 0
                                ? freightBase
                                : freightBase === "#F5F7FF"
                                  ? "#EEF1FF"
                                  : "#FAFAFA";

                            const stickyCell = {
                              bgcolor: `${rowBg} !important`,
                              zIndex: 5,
                            };

                            return (
                              <TableRow
                                key={`${freight.freight_id}_${pkgIdx}`}
                                sx={{
                                  "& > td": {
                                    bgcolor: rowBg,
                                    transition: "background-color 0.12s",
                                    borderBottom: isLast
                                      ? "2px solid #6366F1"
                                      : "1px solid #E2E8F0",
                                  },
                                  "&:hover > td": {
                                    bgcolor: "#E0E7FF !important",
                                  },
                                }}
                              >
                                {/* # */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.68rem",
                                        fontWeight: 700,
                                        color: "#94A3B8",
                                      }}
                                    >
                                      {freightIdx + 1}
                                    </Typography>
                                  </TableCell>
                                )}

                                {/* LR No — sticky */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      position: "sticky",
                                      left: 0,
                                      ...stickyCell,
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <Chip
                                      label={freight.lr_number || "—"}
                                      size="small"
                                      sx={{
                                        fontSize: "0.6rem",
                                        height: 18,
                                        borderRadius: "6px !important",
                                        bgcolor: "#EEF2FF",
                                        color: "#4338CA",
                                        border: "1px solid #C7D2FE",
                                        fontWeight: 700,
                                        "& .MuiChip-label": { px: 0.75 },
                                      }}
                                    />
                                  </TableCell>
                                )}

                                {/* Truck No */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "0.72rem",
                                        color: "#0891B2",
                                      }}
                                    >
                                      {freight.truck_no || "—"}
                                    </Typography>
                                  </TableCell>
                                )}

                                {/* Route */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.68rem",
                                        color: "#334155",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {freight.from_location || "—"} →{" "}
                                      {freight.to_location || "—"}
                                    </Typography>
                                  </TableCell>
                                )}

                                {/* Loading Date */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    {formatDate(freight.loading_date)}
                                  </TableCell>
                                )}

                                {/* Unloading Date */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    {formatDate(freight.unloading_date)}
                                  </TableCell>
                                )}

                                {/* Package */}
                                <TableCell>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      color: "#1E293B",
                                    }}
                                  >
                                    {pkg.packages || "—"}
                                  </Typography>
                                </TableCell>

                                {/* Description */}
                                <TableCell>
                                  <Tooltip title={pkg.description || "—"} arrow>
                                    <Typography
                                      sx={{
                                        fontSize: "0.72rem",
                                        color: "#64748B",
                                        maxWidth: 170,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {pkg.description || "—"}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>

                                {/* Actual Wt */}
                                <TableCell>
                                  {pkg.actual_weight ?? "—"}
                                </TableCell>

                                {/* Charged Wt */}
                                <TableCell>
                                  {pkg.charged_weight ?? "—"}
                                </TableCell>

                                {/* Rate */}
                                <TableCell
                                  sx={{ color: "#7C3AED", fontWeight: 600 }}
                                >
                                  {pkg.rate ?? "—"}
                                </TableCell>

                                {/* Amount */}
                                <TableCell
                                  sx={{ fontWeight: 700, color: "#059669" }}
                                >
                                  {formatCurrency(pkg.amount)}
                                </TableCell>

                                {/* E-way Bill */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    <Tooltip
                                      title={freight.eway_bill_no || "—"}
                                      arrow
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.68rem",
                                          maxWidth: 85,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {freight.eway_bill_no || "—"}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                )}

                                {/* Invoice No */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    {freight.invoice_no || "—"}
                                  </TableCell>
                                )}

                                {/* Goods */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    <Tooltip
                                      title={
                                        freight.classification_of_goods || "—"
                                      }
                                      arrow
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.68rem",
                                          maxWidth: 110,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {freight.classification_of_goods || "—"}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                )}

                                {/* Packing */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      verticalAlign: "middle",
                                      ...stickyCell,
                                    }}
                                  >
                                    {freight.method_of_packing || "—"}
                                  </TableCell>
                                )}

                                {/* Subtotal — sticky right */}
                                {isFirst && (
                                  <TableCell
                                    rowSpan={rowSpan}
                                    sx={{
                                      position: "sticky",
                                      right: 0,
                                      ...stickyCell,
                                      verticalAlign: "middle",
                                      fontWeight: 800,
                                      color: "#059669",
                                      bgcolor: "#ECFDF5 !important",
                                      borderLeft: "1px solid #A7F3D0",
                                    }}
                                  >
                                    {formatCurrency(freight.subtotal)}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          });
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Grand Total Footer */}
                  {rawBillData.freights.length > 0 && (
                    <Box
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        px: 2,
                        py: 1,
                        background:
                          "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
                        borderTop: "2px solid #10B981",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 3,
                        alignItems: "center",
                      }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "#475569",
                            fontWeight: 600,
                          }}
                        >
                          Total LR's:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "#0F172A",
                          }}
                        >
                          {rawBillData.freights.length}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "#475569",
                            fontWeight: 600,
                          }}
                        >
                          Grand Total:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: "#059669",
                          }}
                        >
                          {formatCurrency(
                            rawBillData.freights.reduce(
                              (s, f) => s + (parseFloat(f.subtotal) || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <SettleConfirmPopover
        anchorEl={settleAnchorEl}
        onClose={() => {
          setSettleAnchorEl(null);
          setSettleId(null);
        }}
        onConfirm={handleSettleConfirm}
      />
    </Box>
  );
}
